import React from "react";
import { Card, Input, Space, Select, Switch, Button, InputNumber, Typography, QRCode } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Draggable from "react-draggable";

const { Text } = Typography;

interface LabelElement {
  id: string;
  type: "text" | "barcode" | "qrcode" | "logo" | "line";
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize?: number;
  fontWeight?: string;
  text?: string;
  align?: "left" | "center" | "right";
  barcodeFormat?: "code128" | "qr";
  rotation?: number;
}

interface LabelTemplate {
  id?: string;
  name: string;
  width: number;
  height: number;
  elements: LabelElement[];
  is_default?: boolean;
}

interface WorkspaceCanvasProps {
  activeTemplate: LabelTemplate;
  setActiveTemplate: React.Dispatch<React.SetStateAction<LabelTemplate>>;
  selectedElementId: string | null;
  setSelectedElementId: (id: string | null) => void;
  gridSnap: boolean;
  setGridSnap: (val: boolean) => void;
  zoom: number;
  setZoom: (val: number) => void;
  canvasWidthPx: number;
  canvasHeightPx: number;
  addElement: (type: LabelElement["type"]) => void;
  handleDrag: (id: string, e: any, data: any) => void;
  getElementPreviewText: (text: string) => string;
  generateCode128SvgPath: (text: string) => { path: string; width: number };
  logoBase64: string;
  canvasRef: React.RefObject<HTMLDivElement>;
}

export const WorkspaceCanvas: React.FC<WorkspaceCanvasProps> = ({
  activeTemplate,
  setActiveTemplate,
  selectedElementId,
  setSelectedElementId,
  gridSnap,
  setGridSnap,
  zoom,
  setZoom,
  canvasWidthPx,
  canvasHeightPx,
  addElement,
  handleDrag,
  getElementPreviewText,
  generateCode128SvgPath,
  logoBase64,
  canvasRef
}) => {
  return (
    <Card className="shadow-sm border border-gray-200 rounded-lg bg-white">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
        <Space>
          <Input 
            value={activeTemplate.name}
            onChange={(e) => setActiveTemplate(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Template Name"
            className="font-bold text-base text-gray-800 p-0 hover:bg-gray-50 focus:bg-white px-2 py-1 rounded"
            bordered={false}
          />
        </Space>
        <Space size="middle">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-semibold">Scale Preview</span>
            <Select
              size="small"
              value={zoom}
              onChange={setZoom}
              className="w-20"
              options={[
                { value: 1.0, label: "100%" },
                { value: 1.5, label: "150%" },
                { value: 2.0, label: "200%" },
                { value: 2.5, label: "250%" },
                { value: 3.0, label: "300%" }
              ]}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Grid Snap</span>
            <Switch 
              size="small"
              checked={gridSnap}
              onChange={setGridSnap} 
            />
          </div>
          <Button 
            size="small" 
            onClick={() => {
              setActiveTemplate(prev => ({ ...prev, elements: [] }));
              setSelectedElementId(null);
            }}
            danger
            type="text"
          >
            Clear All
          </Button>
        </Space>
      </div>

      <div className="flex gap-4 mb-4">
        <div>
          <label className="text-[11px] font-semibold text-gray-500 block mb-1">Width (mm)</label>
          <InputNumber 
            min={10} 
            max={200} 
            value={activeTemplate.width}
            onChange={(val) => setActiveTemplate(prev => ({ ...prev, width: val || 50 }))}
            className="w-24 text-xs"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-gray-500 block mb-1">Height (mm)</label>
          <InputNumber 
            min={10} 
            max={200} 
            value={activeTemplate.height}
            onChange={(val) => setActiveTemplate(prev => ({ ...prev, height: val || 30 }))}
            className="w-24 text-xs"
          />
        </div>
        <div className="flex items-end pb-1.5">
          <Text type="secondary" className="text-xs text-gray-400">
            ({Math.round(canvasWidthPx)} × {Math.round(canvasHeightPx)} px)
          </Text>
        </div>
      </div>

      {/* Toolbox */}
      <div className="bg-gray-50 p-2.5 rounded-lg mb-4 flex gap-2 flex-wrap border border-gray-150">
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addElement("text")} className="text-xs">Add Text</Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addElement("barcode")} className="text-xs">Add Barcode</Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addElement("qrcode")} className="text-xs">Add QR Code</Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addElement("logo")} className="text-xs">Add Logo</Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addElement("line")} className="text-xs">Add Line</Button>
      </div>

      {/* Label Canvas Container */}
      <div className="flex justify-center p-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
        <div 
          ref={canvasRef}
          className="relative bg-white border border-gray-400 shadow-lg overflow-hidden select-none transition-all duration-200"
          style={{
            width: `${canvasWidthPx * zoom}px`,
            height: `${canvasHeightPx * zoom}px`,
            backgroundImage: gridSnap ? "radial-gradient(#e5e7eb 1px, transparent 1px)" : "none",
            backgroundSize: `${10 * zoom}px ${10 * zoom}px`,
          }}
          onClick={() => setSelectedElementId(null)}
        >
          {activeTemplate.elements.map((el) => {
            const previewText = getElementPreviewText(el.text || "");
            const isSelected = selectedElementId === el.id;

            return (
              <Draggable
                key={el.id}
                bounds="parent"
                grid={gridSnap ? [5 * zoom, 5 * zoom] : [1, 1]}
                position={{ x: el.x * zoom, y: el.y * zoom }}
                onDrag={(e, data) => handleDrag(el.id, e, data)}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedElementId(el.id);
                  }}
                  className={`absolute cursor-move select-none p-0.5 border ${isSelected ? "border-indigo-600 bg-indigo-50/10 z-10 ring-1 ring-indigo-300" : "border-transparent hover:border-gray-300"}`}
                  style={{
                    width: `${el.w * zoom}px`,
                    height: `${el.h * zoom}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start",
                  }}
                >
                  {el.type === "text" && (
                    <div style={{
                      fontSize: `${(el.fontSize || 10) * zoom}px`,
                      fontWeight: el.fontWeight === "bold" ? 700 : el.fontWeight === "semibold" ? 600 : 400,
                      textAlign: el.align,
                      width: "100%",
                      height: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      transform: `rotate(${el.rotation || 0}deg)`,
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {previewText || "Static Text"}
                    </div>
                  )}
                  {el.type === "barcode" && (
                    <div className="w-full h-full flex items-center justify-center overflow-hidden">
                      {previewText ? (
                        <svg viewBox={`0 0 ${generateCode128SvgPath(previewText).width} 100`} style={{ width: "100%", height: "100%" }} preserveAspectRatio="none">
                          <path d={generateCode128SvgPath(previewText).path} stroke="black" strokeWidth="2" fill="none" />
                        </svg>
                      ) : (
                        <div className="text-[8px] text-gray-400">Barcode</div>
                      )}
                    </div>
                  )}
                  {el.type === "qrcode" && (
                    <div className="w-full h-full flex items-center justify-center overflow-hidden">
                      {previewText ? (
                        <QRCode value={previewText} size={Math.min(el.w, el.h) * zoom - 4} bordered={false} />
                      ) : (
                        <div className="text-[8px] text-gray-400">QR Code</div>
                      )}
                    </div>
                  )}
                  {el.type === "logo" && (
                    <div className="w-full h-full flex items-center justify-center overflow-hidden">
                      {logoBase64 ? (
                        <img src={logoBase64} alt="logo" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="text-[9px] text-gray-400 border border-dashed p-1">Logo</div>
                      )}
                    </div>
                  )}
                  {el.type === "line" && (
                    <div className="w-full h-full bg-black"></div>
                  )}
                </div>
              </Draggable>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
